import React from 'react'
import * as D3 from 'd3'
import { values } from 'lodash'
import { svg } from 'd3'
import { connect } from 'react-redux';

class Chart extends React.Component {
  constructor(props) {
    super(props)
  }

  prepChart(props) {
    const { width, height } = props

    return D3.select(this.chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${40})`)
  }

  render() {
    console.error('Overwrite chart render')
    return null
  }
}

class ElevationChart extends Chart {
  constructor(props) {
    super(props)
    this.d3svg = null
    this.chartRef = React.createRef()
  }

  componentDidMount() {
    const bounds = this.chartRef.current.getBoundingClientRect()
    this.d3svg = this.prepChart({ width: bounds.width, height: bounds.height })

    // Explicitly set SVG dimensions
    const svg = this.chartRef.current.querySelector('svg');
    if (svg) {
      svg.setAttribute('width', bounds.width || 332);
      svg.setAttribute('height', bounds.height || 200);
    }

    this.drawChart({ width: bounds.width || 332, height: bounds.height || 200 })
  }

  componentDidUpdate() {
    if (this.chartRef.current) {
      const bounds = this.chartRef.current.getBoundingClientRect()
      const svg = this.chartRef.current.querySelector('svg');
      if (svg) {
        svg.setAttribute('width', bounds.width || 332);
        svg.setAttribute('height', bounds.height || 200);
      }
      this.drawChart({ width: bounds.width || 332, height: bounds.height || 200 });
    }
  }

  drawChart(props) {
    if (!this.d3svg) {
      console.error("SVG element not initialized.");
      return;
    }

    const { xKey, yKey, data, userPreferences, geoJson } = this.props;
    const roundToNearest = (value, base = 10) => Math.ceil(value / base) * base;

    let distance = data[data.length - 1][0];
    let distanceInMiles = distance * 0.00062137;

    let yScaleValue = userPreferences.units === "imperial" ? 3.28084 : 1;
    let xScaleValue = userPreferences.units === "imperial"
      ? (distance < 1609.34 * 0.5 ? 0.9144 : 1609.34)
      : 1;

    const minYValue = Math.floor((D3.min(data, d => +d[yKey]) / 10) * 10) * yScaleValue;
    const maxYValue = Math.ceil((D3.max(data, d => +d[yKey]) / 10) * 10) * yScaleValue;

    const exactValue = distance / xScaleValue;
    let maxXValue = roundToNearest(exactValue)
    // Create xScale
    const xScale = D3.scaleLinear()
      .domain([0, exactValue])
      .range([0, props.width - 56]);

    // Create yScale
    const yScale = D3.scaleLinear()
      .domain([minYValue, maxYValue])
      .range([props.height - 35, 5]);

    // Clear previous elements
    this.d3svg.selectAll("*").remove();

    // Append X-Axis
    this.d3svg.append('g')
      .attr('transform', `translate(0, ${props.height - 30})`)
      .call(D3.axisBottom(xScale)
        .tickValues([0, exactValue / 2, exactValue]) // Ensure distinct tick values
        .tickFormat(d => {
          let distStr;
          if (userPreferences.units === 'imperial') {
            if (distance < 804.672) {
              distStr = `${Math.round(d).toLocaleString()}yd`; // Display in yards
            } else {
              distStr = `${d.toFixed(1)}mi`;
            }
          } else {
            if (distance < 1000) {
              distStr = `${d.toFixed(0)}mts`;
            } else {
              distStr = `${(d / 1000).toFixed(1)}km`;
            }
          }
          return distStr;
        })
      );

    // Append Y-Axis
    this.d3svg.append('g')
      .call(D3.axisLeft(yScale))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 10)
      .attr('fill', '#959595')
      .text(userPreferences.units === 'imperial' ? 'Elevation (feet)' : 'Elevation (mts)');

    // Draw Area Path
    this.d3svg.append('path')
      .datum(data)
      .attr('fill', D3.rgb(144, 255, 0, 0.3))
      .attr('stroke', D3.rgb(144, 255, 0, 1))
      .attr('stroke-width', 2)
      .attr('d', D3.area()
        .x(d => xScale(d[0] / xScaleValue))
        .y0(yScale(minYValue))
        .y1(d => yScale(d[1] * yScaleValue))
      );
  }

  render() {
    return (
      <div style={{ width: "100%", height: "210px" }}>
        <div
          style={{ width: "100%", height: "200px" }}
          id="d3-chart-container"
          ref={this.chartRef}
        />
      </div>
    );
  }
}

class BarChart extends Chart {
  constructor(props) {
    super(props)
    this.d3svg = null
    this.chartRef = React.createRef()
  }


  componentDidMount() {
    const bounds = this.chartRef.current.getBoundingClientRect()
    this.d3svg = this.prepChart({ width: Math.floor(bounds.width), height: Math.floor(bounds.height) })
    this.drawChart({ width: bounds.width, height: bounds.height })
    // this.drawChart({ width: bounds.width, height: bounds.height })
  }

  drawChart(props) {
    const { xKey, yKey, data } = this.props
    this.x = D3.scaleBand()
      .domain(data.map(d => d[xKey]))
      .range([0, (props.width - 40)])
      .padding(0.1)

    this.y = D3.scaleLinear()
      .domain([D3.max(data, d => d[yKey]), 0])
      .nice()
      .range([10, props.height - 20])

    const selection = this.d3svg.selectAll('g')
      .data('data')
      .join('g')

    selection
      .data(data)
      .append('rect')
      .attr('fill', this.props.barColor)
      .attr('width', d => this.x.bandwidth())
      .attr('height', d => this.y(0) - this.y(d[yKey]))
      .attr('x', d => this.x(d[xKey]))
      .attr('y', d => this.y(d[yKey]))

    selection
      .data(data)
      .append('text')
      .attr('fill', 'white')
      .attr('width', d => this.x.bandwidth())
      .attr('font-size', 10)
      .attr('y', d => this.y(d[yKey]) + 10)
      .attr('x', d => this.x(d[xKey]) + 1)
      .text(d => d[yKey])

    this.d3svg.append('g')
      .attr('transform', `translate(0, ${props.height - 20})`)
      .call(D3.axisBottom(this.x))

    this.d3svg.append('g')
      .attr('transform', `translate(0, 10})`)
      .call(D3.axisLeft(this.y))
      .append('text')
      .attr('fill', '#959595')
  }

  render() {
    return (
      <div style={{ width: '100%', height: '100%' }}>
        <div style={{
          width: '100%',
          height: '100%'
        }}
          ref={this.chartRef}></div>
      </div>
    )
  }
}

class StackedBarChart extends Chart {
  constructor(props) {
    super(props)
    this.d3svg = null
    this.chartRef = React.createRef()
  }


  componentDidMount() {
    const bounds = this.chartRef.current.getBoundingClientRect()
    this.d3svg = this.prepChart({ width: Math.floor(bounds.width), height: Math.floor(bounds.height) })
    this.drawChart({ width: bounds.width, height: bounds.height })
  }

  drawChart(props) {
    const { xKey, yKey, data } = this.props
    this.x = D3.scaleLinear()
      .domain(data.map(d => d[xKey]))
      .range([0, (props.width - 40)])
      .padding(0.1)

    this.y = D3.scaleLinear()
      .domain([D3.max(data, d => d[yKey]), 0])
      .nice()
      .range([10, props.height - 20])

    const selection = this.d3svg.selectAll('g')
      .data('data')
      .join('g')

    selection
      .data(data)
      .append('rect')
      .attr('fill', this.props.barColor)
      .attr('width', d => this.x.bandwidth())
      .attr('height', d => this.y(0) - this.y(d[yKey]))
      .attr('x', d => this.x(d[xKey]))
      .attr('y', d => this.y(d[yKey]))

    selection
      .data(data)
      .append('text')
      .attr('fill', 'white')
      .attr('width', d => this.x.bandwidth())
      .attr('font-size', 10)
      .attr('y', d => this.y(d[yKey]) + 10)
      .attr('x', d => this.x(d[xKey]) + 1)
      .text(d => d[yKey])

    this.d3svg.append('g')
      .attr('transform', `translate(0, ${props.height - 20})`)
      .call(D3.axisBottom(this.x))

    this.d3svg.append('g')
      .attr('transform', `translate(0, 10})`)
      .call(D3.axisLeft(this.y))
      .append('text')
      .attr('fill', '#959595')
  }

  render() {
    return (
      <div style={{ width: '100%', height: '100%' }}>
        <div style={{
          width: '100%',
          height: '100%'
        }}
          ref={this.chartRef}></div>
      </div>
    )
  }
}

class PieChart extends Chart {
  constructor(props) {
    super(props)
    this.d3svg = null
    this.chartRef = React.createRef()
  }

  componentDidMount() {
    const bounds = this.chartRef.current.getBoundingClientRect()
    this.d3svg = this.prepChart({ width: Math.floor(bounds.width), height: Math.floor(bounds.height) })
    this.drawChart({ width: bounds.width, height: bounds.height })
  }

  prepChart(props) {
    return D3.select(this.chartRef.current)
      .append('svg')
      .attr('viewBox', [-props.width / 2, -props.height / 2, props.width, props.height])
  }

  drawChart(props) {
    const { valKey, nameKey, data, colors } = this.props,
      radius = Math.floor(Math.min(props.width, props.height) / 2),
      pie = D3.pie()
        .sort(null)
        .value(d => d[valKey]),
      arc = D3.arc()
        .innerRadius(0)
        .outerRadius(radius * 0.5),
      pieData = pie(data),
      textArc = D3.arc().innerRadius(radius * 0.7).outerRadius(radius * 0.7)

    this.d3svg.append('g')
      .selectAll('path')
      .data(pieData)
      .join('path')
      .attr('fill', d => colors[d.index])
      .attr('d', arc)

    this.d3svg.append('g')
      .attr('font-size', 10)
      .attr('text-anchor', 'middle')
      .selectAll('text')
      .data(pieData)
      .join('text')
      .attr('fill', 'white')
      .attr('transform', d => `translate(${textArc.centroid(d)})`)
      .attr('font-weight', 'bold')
      .text(d => d.data[nameKey])

  }

  render() {
    return (
      <div style={{ width: '100%', height: '100%' }}>
        <div style={{
          width: '100%',
          height: '100%'
        }}
          ref={this.chartRef}></div>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  userPreferences: state.userPreferences,
})

const mapDispatchToProps = dispatch => ({

})
// Connect ElevationChart component to Redux
const ConnectedElevationChart = connect(mapStateToProps, mapDispatchToProps)(ElevationChart);

export { StackedBarChart, ElevationChart, BarChart, PieChart, ConnectedElevationChart }
